import React, { useCallback, useRef, useState } from 'react';
import { UnlockDialog } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog';
import { useDisclosure } from '@withjoy/joykit';
import { withWindow } from '@shared/utils/withWindow';
import { UnlockDialogContext } from './UnlockDialog.context';

export const UnlockDialogProvider: React.FC = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [shouldDisplayFindInvite, setShouldDisplayFindInvite] = useState<boolean>(false);
  const [shouldSkipPasswordGate, setShouldSkipPasswordGate] = useState<boolean>(false);
  // Ref, not state: simple-flow auto-close calls handleClose synchronously
  // after markUnlocked, so we need the latest value without waiting for a re-render.
  const didUnlockRef = useRef<boolean>(false);

  const markUnlocked = useCallback(() => {
    didUnlockRef.current = true;
  }, []);

  const handleUnlockDialogOpen = (displayFindInvite?: boolean, skipPasswordGate?: boolean) => {
    setShouldDisplayFindInvite(displayFindInvite ?? false);
    setShouldSkipPasswordGate(skipPasswordGate ?? false);
    didUnlockRef.current = false;
    onOpen();
  };

  const handleClose = useCallback(() => {
    if (didUnlockRef.current) {
      // Full page reload — every query refetches with the unlocked auth context.
      // Simpler and more durable than tracking which Apollo queries to invalidate.
      withWindow(global => global.location.reload());
    }
    // Always run onClose: in production the reload discards the heap before this
    // commits; in JSDOM (reload no-ops) this keeps React state consistent so
    // existing spec assertions of "dialog closes after unlock" still hold.
    onClose();
  }, [onClose]);

  return (
    <UnlockDialogContext.Provider value={{ isOpen, handleUnlockDialogOpen, onClose: handleClose, shouldDisplayFindInvite, markUnlocked }}>
      {children}
      <UnlockDialog
        isOpen={isOpen}
        onClose={handleClose}
        shouldDisplayFindInvite={shouldDisplayFindInvite}
        shouldSkipPasswordGate={shouldSkipPasswordGate}
      />
    </UnlockDialogContext.Provider>
  );
};

export { UnlockDialogContext, useUnlockDialogContext } from './UnlockDialog.context';
export type { UnlockDialogContextValue } from './UnlockDialog.context';
